import styles from './style.module.css';
import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { Markup } from 'interweave';
import { Button } from '../../components/Button';
import { HelmetComponent } from '../../components/Helmet';
import { IFormValues } from './components/interfaces';
import { Notify } from '../../components/Notify';
import { Card } from '../../components/Card';
import { List, ListItem } from '../../components/List';
import { Input } from '../../components/forms/Input';
import { ModalConfirm } from './components/ModalConfirm';
import { api } from '../../server/api';
import { Banks } from '../../constants/Banks';
import { MaskCNPJ, MaskCPF, maskTEL } from '../../utils/mask';
import { useAuth } from '../../hooks/useAuth';
import bankImage from '../../assets/images/bank.png';
import pixImage from '../../assets/images/pix.png';

export function PaymentLead() {
  const { setClient, client } = useAuth();
  const { clientId, leadId } = useParams();

  const [paymentMethod, setPaymentMethod] = useState('');
  const [typePix, setTypePix] = useState('');
  const [maxLength, setMaxLength] = useState<number | undefined>(undefined);

  const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<IFormValues>();

  useEffect(() => {
    getClientById();
  }, []);

  const getClientById = async () => {
    const parameters = {
      userType: 'checkoutLead',
      searchFunctionality: 'searchClient',
      clientId: clientId,
    };

    const { data } = await api.post('/checkout', parameters);
    if (data.success) {
      setClient(data.data);
    }
  };

  const handleForm = handleSubmit(async (dataForm) => {
    try {
      let formData: any = {};
      if (paymentMethod === 'pix') {
        formData = {
          accountData: {
            paymentMethod,
            pixkey: dataForm.pixkey,
            typePix,
          },
        };
      } else {
        formData = {
          accountData: {
            paymentMethod,
            name: dataForm.name,
            bank: dataForm.bank,
            accountType: dataForm.accountType,
            agency: dataForm.agencyNumber,
            conta: dataForm.accountNumber,
            digitCount: dataForm.digitAccount,
            digitAgency: dataForm.digitAgency,
            cpf: dataForm.cpf,
          },
        };
      }

      formData = {
        ...formData,
        typeRescue: 'rescueLead',
      };

      const parameters = {
        createFunctionality: 'paymentLead',
        userType: 'paymentLead',
        formData,
        clientId: client.id,
        leadId,
      };

      const { data } = await api.post('/checkout', parameters);
      if (data.success) {
        toggleModal();
      } else {
        return Notify(data.message, 'error');
      }
    } catch (error) {
      console.log('handleForm error', error);
    }
  });

  const toggleModal = () => {
    setModalIsOpen(!modalIsOpen);
  };

  const handleChange = (type: string) => {
    //929.324.090-42 cpf 14
    //77.996.783/0001-30 cnpj 18
    //(86) 99979-7405 telefone 15
    //2772b87c-b909-4e42-903b-4eab0ff57fff chave aleatória 32
    if (type === 'CPF') {
      setTypePix('CPF');
      setMaxLength(14);
      setValue('pixkey', '');
    }
    if (type === 'CNPJ') {
      setTypePix('CNPJ');
      setMaxLength(18);
      setValue('pixkey', '');
    }
    if (type === 'TELEFONE') {
      setTypePix('TELEFONE');
      setMaxLength(15);
      setValue('pixkey', '');
    }
    if (type === 'CHAVE_ALEATORIA') {
      setTypePix('CHAVE_ALEATORIA');
      setMaxLength(36);
      setValue('pixkey', '');
    }
    if (type === 'EMAIL') {
      setTypePix('EMAIL');
      setMaxLength(undefined);
      setValue('pixkey', '');
    }
  };

  const renderPaymentPix = () => {
    return (
      <>
        <div className="mt-3">
          <span className="fs-4 fw-semibold">Selecione sua chave pix</span>
        </div>
        <div className="mt-3">
          <div className="row">
            <div className="col-sm-12 col-md-6">
              <List>
                <ListItem
                  handleChange={() => handleChange('CPF')}
                  icon="person"
                  content="CPF"
                  rangeChecked={typePix === 'CPF'}
                  withRange={true}
                />
                <ListItem
                  handleChange={() => handleChange('CNPJ')}
                  icon="business"
                  content="CNPJ"
                  rangeChecked={typePix === 'CNPJ'}
                  withRange={true}
                />
                <ListItem
                  handleChange={() => handleChange('TELEFONE')}
                  icon="phone"
                  content="Telefone"
                  rangeChecked={typePix === 'TELEFONE'}
                  withRange={true}
                />
                <ListItem
                  handleChange={() => handleChange('EMAIL')}
                  icon="mail"
                  content="E-mail"
                  rangeChecked={typePix === 'EMAIL'}
                  withRange={true}
                />
                <ListItem
                  handleChange={() => handleChange('CHAVE_ALEATORIA')}
                  icon="format_color_text"
                  content="Chave aleatória"
                  rangeChecked={typePix === 'CHAVE_ALEATORIA'}
                  withRange={true}
                />
              </List>
            </div>
            <div className="col-sm-12 col-md-6">
              <div className="my-3">
                <Input
                  maxLength={maxLength}
                  type="text"
                  label="Chave PIX"
                  placeholder={
                    typePix === 'CHAVE_ALEATORIA'
                      ? 'Copie e cole aqui'
                      : 'Digite sua chave Pix'
                  }
                  disabled={!typePix}
                  readOnly={!typePix}
                  {...register('pixkey', {
                    required: true,
                    pattern: {
                      value:
                        typePix === 'EMAIL'
                          ? /^[A-Z0-9._%+-]+@[A-Z0-9.-]+.[A-Z]{2,}$/i
                          : /./,
                      message:
                        typePix === 'EMAIL' ? 'invalid email address' : '',
                    },
                  })}
                  errors={errors.pixkey ? 'Campo obrigatório' : ''}
                  onChange={(event) => {
                    const { value } = event.target;
                    if (typePix === 'CPF') {
                      if (value.length <= 14) {
                        event.target.value = MaskCPF(value);
                      }
                    }
                    if (typePix === 'CNPJ') {
                      if (value.length > 14) {
                        event.target.value = MaskCNPJ(value);
                      }
                    }
                    if (typePix === 'TELEFONE') {
                      event.target.value = maskTEL(value);
                    }
                    if (typePix === 'CHAVE_ALEATORIA') {
                      event.target.value = value;
                    }
                    if (typePix === 'EMAIL') {
                      event.target.value = value;
                    }
                  }}
                />
              </div>
              <div className="mt-3">
                <div className="alert alert-warning" role="alert">
                  <h4 className="alert-heading">Informação sobre o resgate</h4>
                  <br />
                  <ul>
                    <li>
                      Os dados de resgate via PIX ou TED, devem corresponder ao
                      mesmo CPF ou CNPJ do usuário cadastrado na plataforma.
                    </li>
                    <li>
                      Não é permitido utilizar contas de terceiros para realizar
                      o resgate.
                    </li>
                    <li>
                      Informe seus dados de resgate{' '}
                      <strong>corretamente</strong> e revise-os antes de
                      finalizar o resgate.
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  };

  const renderPaymentTed = () => {
    return (
      <>
        <div className="mt-3">
          <span className="fs-4 fw-semibold">
            Preencha seus dados bancários
          </span>
        </div>
        <div className="mt-3">
          <div className="row">
            <div className="col-sm-12 col-md-6">
              <Input
                type="text"
                label="Nome"
                placeholder="Digite seu Nome"
                {...register('name', {
                  required: true,
                })}
                errors={errors.name ? 'Campo obrigatório' : ''}
              />
            </div>
            <div className="col-sm-12 col-md-6">
              <Input
                maxLength={14}
                type="text"
                label="CPF"
                placeholder="Digite seu CPF"
                {...register('cpf', {
                  required: true,
                })}
                errors={errors.cpf ? 'Campo obrigatório' : ''}
                onChange={(event) => {
                  const { value } = event.target;

                  if (value.length <= 14) {
                    event.target.value = MaskCPF(value);
                  }
                  // else {
                  //   console.log('é cnpj');
                  //   event.target.value = MaskCNPJ(value);
                  // }
                }}
              />
            </div>
            <div className="col-sm-12 col-md-6">
              <label htmlFor="accountType">Tipo de conta</label>
              <select
                className={`${styles.select}`}
                {...register('accountType', {
                  required: true,
                })}
                aria-invalid={errors.accountType ? 'true' : 'false'}
              >
                <option value="">Selecione o tipo de conta</option>
                <option value="CONTA_CORRENTE">Conta Corrente</option>
                <option value="CONTA_POUPANCA">Conta Poupança</option>
                <option value="CONTA_FACIL">Conta Fácil</option>
                <option value="ENTIDADES_PUBLICAS">Entidades Públicas</option>
              </select>
              <span className={styles.error}>
                {errors.accountType ? 'Campo obrigatório' : ''}
              </span>
            </div>
            <div className="col-sm-12 col-md-6">
              <label htmlFor="bank">Tipo de conta</label>
              <select
                className={`${styles.select}`}
                {...register('bank', {
                  required: true,
                })}
                aria-invalid={errors.bank ? 'true' : 'false'}
                onChange={(e) => {
                  const labelBank =
                    e.target.options[e.target.selectedIndex].text;
                  setValue(`bank`, e.target.value);
                  setValue(`labelBank`, labelBank);
                }}
              >
                <option value="">Escolha seu banco</option>
                {Banks.map((b) => {
                  return (
                    <option key={b.value} value={b.value}>
                      {b.value} - {b.label}
                    </option>
                  );
                })}
              </select>
              <span className={styles.error}>
                {errors.accountType ? 'Campo obrigatório' : ''}
              </span>
            </div>
            <div className="col-sm-12 col-md-6">
              <div className="row">
                <div className="col-6">
                  <Input
                    type="text"
                    label="Número da conta"
                    placeholder="Digite o número da conta"
                    {...register('conta', {
                      required: true,
                    })}
                    errors={errors.conta ? 'Campo obrigatório' : ''}
                    maxLength={13}
                  />
                </div>
                <div className="col-6">
                  <Input
                    type="text"
                    label="Dígito da conta"
                    placeholder="Digite o dígito da conta"
                    {...register('digitCount', {
                      required: true,
                    })}
                    errors={errors.digitCount ? 'Campo obrigatório' : ''}
                    maxLength={1}
                  />
                </div>
              </div>
            </div>
            <div className="col-sm-12 col-md-6">
              <div className="row">
                <div className="col-6">
                  <Input
                    type="text"
                    label="Número da agência"
                    placeholder="Digite o número da agência"
                    {...register('agency', {
                      required: true,
                    })}
                    errors={errors.agency ? 'Campo obrigatório' : ''}
                    maxLength={4}
                  />
                </div>
                <div className="col-6">
                  <Input
                    type="text"
                    label="Dígito da agência"
                    placeholder="Digite o dígito da agência"
                    {...register('digitAgency', {
                      required: true,
                    })}
                    errors={errors.digitAgency ? 'Campo obrigatório' : ''}
                    maxLength={1}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="mt-3">
            <div className="alert alert-warning" role="alert">
              <h4 className="alert-heading">Informação sobre o resgate</h4>
              <br />
              <ul>
                <li>
                  Os dados de resgate via PIX ou TED, devem corresponder ao
                  mesmo CPF ou CNPJ do usuário cadastrado na plataforma.
                </li>
                <li>
                  Não é permitido utilizar contas de terceiros para realizar o
                  resgate.
                </li>
                <li>
                  Informe seus dados de resgate <strong>corretamente</strong> e
                  revise-os antes de finalizar o resgate.
                </li>
              </ul>
            </div>
          </div>
        </div>
      </>
    );
  };

  const verifyDisabled = () => {
    if (!paymentMethod) {
      return true;
    }

    if (paymentMethod === `pix` && !typePix) {
      return true;
    }
    return false;
  };

  return (
    <>
      <HelmetComponent
        clientLogoURL={client?.logoURL}
        companyName={client?.companyName}
        clientFavicon={client?.favIcon}
        primaryColor={client?.primaryColor}
        secondaryColor={client?.secondaryColor}
        tabTitle={`Checkout`}
      />

      <div className="container">
        <div className="container">
          <div className="py-5 text-center">
            <img
              className="d-block mx-auto mb-4"
              src={client?.logoURL}
              alt=""
              height="80"
            />

            <p className="lead">
              Preencha os dados abaixo e os dados corretos para pagamento.
              Verifique os campos obrigatórios.
            </p>
          </div>

          <form onSubmit={handleForm}>
            <div className="row">
              <div className="col-sm-12">
                <div className="mb-3">
                  <Card>
                    <List>
                      <ListItem
                        handleChange={() => setPaymentMethod('pix')}
                        image={pixImage}
                        content="Pagamento via PIX"
                        rangeChecked={paymentMethod === 'pix'}
                        withRange={true}
                        inputType="radio"
                      />
                      <ListItem
                        handleChange={() => setPaymentMethod('ted')}
                        image={bankImage}
                        content="Pagamento via TED"
                        rangeChecked={paymentMethod === 'ted'}
                        withRange={true}
                        inputType="radio"
                      />
                    </List>

                    {paymentMethod === 'pix' && renderPaymentPix()}
                    {paymentMethod === 'ted' && renderPaymentTed()}

                    <div className="d-flex flex-row-reverse me-check justify-content-end mt-3">
                      <Input
                        type="checkbox"
                        label={
                          <Markup
                            content={`Li e concordo com a <a class="fw-bold"  target="_blank"
                          href="https://firebasestorage.googleapis.com/v0/b/oqhomapi.appspot.com/o/TermsAndPolitics%2FPoli%CC%81tica%20de%20Privacidade_OQ%20MIDIA%20DIGITAL%20JULHO%202020.docx.pdf?alt=media&token=73c99224-963a-40c8-b385-618bb2cc5122">Política de Privacidade</a> e aceito os termos definidos.`}
                          />
                        }
                        {...register('acceptedTerms', {
                          required: true,
                        })}
                        style={{
                          marginRight: '15px',
                        }}
                      />
                    </div>

                    <span className={`${styles.errorAccount}`}>
                      {errors.acceptedTerms ? 'Campo obrigatório' : ''}
                    </span>
                    <div className="d-flex justify-content-center p-4">
                      <Button
                        type="submit"
                        text="Finalizar"
                        color="primary"
                        icon="check"
                        disabled={verifyDisabled() || isSubmitting}
                        isLoading={isSubmitting}
                      />
                    </div>
                  </Card>
                </div>
              </div>
            </div>
          </form>

          <footer className="my-5 pt-5 text-muted text-center text-small">
            <p className="mb-1">&copy; 2021-2022 OQ Digital</p>
            <ul className="list-inline">
              <li className="list-inline-item">
                <a href="#">Privacidade</a>
              </li>
              <li className="list-inline-item">
                <a href="#">Termos</a>
              </li>
              <li className="list-inline-item">
                <a href="#">Suporte</a>
              </li>
            </ul>
          </footer>
        </div>
      </div>

      <ModalConfirm
        // modalIsOpen={true}
        modalIsOpen={modalIsOpen}
        toggleModal={toggleModal}
        clientUrl={client?.clientUrl ? client?.clientUrl : ''}
        clientNumberOfDaysToRedeem={
          client && client.numberOfDaysToRedeem
            ? `até ${client.numberOfDaysToRedeem} dias úteis`
            : 'alguns dias'
        }
      />
    </>
  );
}
